import { useQuery } from '@tanstack/react-query'
import { ingressCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Link } from '@/ui/Link/Link'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { StatusBadge, StatusDot } from '@/ui/Status/Status'
import { ApiError } from '../../api/client'
import {
  type Delivery,
  type DeliveryRoute,
  ingressApi,
} from '../../api/ingress'
import { ROUTES } from '../../constants/routes'
import { useTopicsQuery } from '../../hooks/useTopicsQuery'
import { formatDateTime } from '../../utils/time'
import styles from './DeliveryTracePanel.module.css'
import { invocationText, outcomeInfo } from './deliveryText'

interface Props {
  workspaceId: string
  resourceId: string
}

/**
 * Recent deliveries to a connection (any kind that receives events) and
 * what each route did with them — the "why didn't my agent fire?" view
 * over the route ledger.
 */
export function DeliveryTracePanel({ workspaceId, resourceId }: Props) {
  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['deliveries', workspaceId, resourceId],
    queryFn: () => ingressApi.deliveries(workspaceId, resourceId),
    staleTime: 5_000,
  })
  const showSkeleton = useSkeletonDelay(isPending)
  const { data: topics = [] } = useTopicsQuery(workspaceId)
  const topicName = (id: number) =>
    topics.find((t) => t.topic_id === String(id))?.name ??
    ingressCopy.trace.topicFallback(id)
  const deliveries = data?.deliveries ?? []

  // 400: this row's kind receives events but this row doesn't (e.g. a
  // token-only connection) — there is no trace to show.
  if (error instanceof ApiError && error.status === 400) return null

  return (
    <Stack as="section" gap="space-12" aria-busy={isPending}>
      <Inline gap="space-8" justify="between" wrap={false}>
        <SectionHeader
          rule
          trailingSpacing="compact"
          className={styles.heading}
        >
          {ingressCopy.trace.heading}
        </SectionHeader>
        <Button
          variant="ghost"
          loading={isFetching}
          onClick={() => void refetch()}
        >
          {ingressCopy.trace.refresh}
        </Button>
      </Inline>
      {data?.last_rejection && (
        <Alert tone="warning">
          {ingressCopy.trace.rejected(
            data.last_rejection.count,
            formatDateTime(data.last_rejection.last_at),
            data.last_rejection.reason,
          )}
        </Alert>
      )}
      {isPending ? (
        showSkeleton && <Skeleton lines={3} />
      ) : isError && !data ? (
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline">
            {ingressCopy.trace.loadFailed}
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {ingressCopy.retry}
            </Button>
          </Inline>
        </Alert>
      ) : deliveries.length === 0 ? (
        <EmptyState
          title={ingressCopy.trace.emptyTitle}
          description={ingressCopy.trace.emptyDescription}
        />
      ) : (
        <RowList hover={false}>
          {deliveries.map((d) => (
            <DeliveryItem
              key={d.delivery_key}
              delivery={d}
              topicName={topicName}
            />
          ))}
        </RowList>
      )}
    </Stack>
  )
}

function DeliveryItem({
  delivery: d,
  topicName,
}: {
  delivery: Delivery
  topicName: (id: number) => string
}) {
  const outcome = outcomeInfo(d)
  return (
    <RowListItem>
      <Stack gap="space-4" className={styles.main}>
        <Inline gap="space-8" justify="between">
          <Inline gap="space-8">
            <StatusBadge tone={outcome.tone}>{outcome.label}</StatusBadge>
            <Text variant="code" tone="text">
              {d.event_type ?? ingressCopy.trace.noType}
            </Text>
            {d.correlation_key && (
              <Text variant="code" tone="text-mute">
                {d.correlation_key}
              </Text>
            )}
            {d.entity && (
              <Text variant="caption" tone="text-mute">
                {ingressCopy.trace.item(d.entity)}
              </Text>
            )}
          </Inline>
          <Text variant="caption" tone="text-mute" numeric>
            {formatDateTime(d.first_seen_at)}
          </Text>
        </Inline>
        {outcome.explain && (
          <Text as="p" variant="caption" tone="text-mute">
            {outcome.explain}
          </Text>
        )}
        {d.routes.length > 0 && (
          <Stack as="ul" gap="space-4" className={styles.routes}>
            {d.routes.map((r) => (
              <RouteRow key={r.route_id} route={r} topicName={topicName} />
            ))}
          </Stack>
        )}
      </Stack>
    </RowListItem>
  )
}

function RouteRow({
  route: r,
  topicName,
}: {
  route: DeliveryRoute
  topicName: (id: number) => string
}) {
  const inv = invocationText(r)
  return (
    <Inline as="li" gap="space-8" align="baseline">
      <Link variant="quiet" href={ROUTES.resourceDetail(r.route_id)}>
        {r.route_name ?? r.route_id}
      </Link>
      <Link
        variant="quiet"
        href={
          r.thread_id
            ? ROUTES.topicThread(String(r.topic_id), r.thread_id)
            : ROUTES.topic(String(r.topic_id))
        }
      >
        {ingressCopy.trace.topic(topicName(r.topic_id))}
      </Link>
      <StatusDot tone={inv.tone} label={inv.text} />
      {r.error && (
        <Text variant="caption" tone="text-mute" className={styles.error}>
          {r.error}
        </Text>
      )}
    </Inline>
  )
}
